'use client';
// SportsMatchCard Component - Sports match prediction card
import { useCallback } from 'react';
import Link from 'next/link';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import { useEventBookmarkAction, useT } from '@/hooks';

import { ROUTES } from '@/constants';

interface Team {
  id: string;
  name: string;
  logo?: React.ReactNode;
  percentage: number;
}

interface SportsMatchCardProps {
  id: string;
  team1: Team;
  team2: Team;
  volume: string;
  league: string;
  dateTime: string;
  imageUrl?: string;
  selectedOutcome?: 'team1' | 'draw' | 'team2';
  onTradeClick?: (matchId: string, outcome: 'team1' | 'draw' | 'team2') => void;
}

export function SportsMatchCard({
  id,
  team1,
  team2,
  volume,
  league,
  dateTime,
  imageUrl,
  selectedOutcome,
  onTradeClick,
}: SportsMatchCardProps) {
  const theme = useTheme();
  const { t } = useT();

  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();
  const isBookmarked = checkIsBookmarked(id);

  function createTradeHandler(outcome: 'team1' | 'draw' | 'team2') {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onTradeClick?.(id, outcome);
    };
  }

  function handleBookmarkClick(event: React.MouseEvent<HTMLButtonElement>) {
    handleBookmarkToggle(event, { id } as any, 'sport');
  }

  function getTeamButtonStyle(outcome: 'team1' | 'draw' | 'team2') {
    const isSelected = selectedOutcome === outcome;
    if (isSelected) {
      return {
        bgcolor: 'error.bg',
        color: 'error.main',
        border: outcome === 'draw' ? '1px solid' : 'none',
        borderColor: outcome === 'draw' ? 'error.border' : 'transparent',
        '&:hover': {
          bgcolor: 'error.bg',
          opacity: 0.9,
          border: outcome === 'draw' ? '1px solid' : 'none',
          borderColor: outcome === 'draw' ? 'error.border' : 'transparent',
        },
      };
    }

    if (outcome === 'draw') {
      return {
        bgcolor: 'background.subtle',
        color: 'text.secondary',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'background.hover',
          borderColor: 'text.disabled',
        },
      };
    }

    return {
      bgcolor: 'background.hover',
      color: 'text.primary',
      border: 'none',
      '&:hover': {
        bgcolor: 'background.subtle',
        border: 'none',
        opacity: 0.8,
      },
    };
  }

  return (
    <Link href={ROUTES.MARKET_DETAILS(id)} style={{ textDecoration: 'none' }}>
      <Card
        sx={{
          height: 220,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: (theme: Theme) =>
              theme.palette.mode === 'dark'
                ? '0 4px 12px rgba(0,0,0,0.4)'
                : '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            p: 1.5,
            overflow: 'hidden',
            '&:last-child': { pb: 1.5 },
          }}
        >
          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              mb: 1,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '10px' },
            }}
          >
            {/* Top Header - Image and Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              {imageUrl && (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={league}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '8px',
                    objectFit: 'contain',
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  {league}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  {dateTime}
                </Typography>
              </Box>
            </Box>

            {/* Team 1 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  mr: 1,
                }}
              >
                {team1.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  flexShrink: 0,
                }}
              >
                {team1.percentage}%
              </Typography>
            </Box>

            {/* Team 2 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  mr: 1,
                }}
              >
                {team2.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  flexShrink: 0,
                }}
              >
                {team2.percentage}%
              </Typography>
            </Box>
          </Box>

          {/* Bottom Container for Actions and Footer */}
          <Box sx={{ mt: 'auto' }}>
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                onClick={createTradeHandler('team1')}
                sx={{
                  flex: 1,
                  py: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  ...getTeamButtonStyle('team1'),
                }}
              >
                {team1.name.split(' ')[0]}
              </Button>
              <Button
                variant="outlined"
                onClick={createTradeHandler('draw')}
                sx={{
                  flex: 1,
                  py: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  ...getTeamButtonStyle('draw'),
                }}
              >
                {t('sports.draw', { defaultValue: 'Draw' }).toUpperCase()}
              </Button>
              <Button
                variant="outlined"
                onClick={createTradeHandler('team2')}
                sx={{
                  flex: 1,
                  py: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  ...getTeamButtonStyle('team2'),
                }}
              >
                {team2.name.split(' ')[0]}
              </Button>
            </Box>

            {/* Footer - Volume and Bookmark */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {volume} Vol.
              </Typography>
              <IconButton
                size="small"
                onClick={handleBookmarkClick}
                sx={{
                  color: isBookmarked ? 'primary.main' : 'text.secondary',
                  p: 0.5,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: isBookmarked ? 'primary.dark' : 'text.primary',
                  },
                }}
                aria-label="Save"
              >
                {isBookmarked ? (
                  <BookmarkIcon sx={{ fontSize: '1.25rem' }} />
                ) : (
                  <BookmarkBorderIcon sx={{ fontSize: '1.25rem' }} />
                )}
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
}

export default SportsMatchCard;
